/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { CoreApiConfiguration } from '../core-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { DiagramResponse as ApiDiagramResponse } from '../models/api/diagram-response';
import { ListTargetsResponse as ApiListTargetsResponse } from '../models/api/list-targets-response';
import { ListTransitionElementResponse as ApiListTransitionElementResponse } from '../models/api/list-transition-element-response';
import { ProgressResponse as ApiProgressResponse } from '../models/api/progress-response';
import { StoryResponse as ApiStoryResponse } from '../models/api/story-response';
import { TransitionElementResponse as ApiTransitionElementResponse } from '../models/api/transition-element-response';
import { createAssessment } from '../fn/transition-element/create-assessment';
import { CreateAssessment$Params } from '../fn/transition-element/create-assessment';
import { createTarget } from '../fn/transition-element/create-target';
import { CreateTarget$Params } from '../fn/transition-element/create-target';
import { createTransitionElement } from '../fn/transition-element/create-transition-element';
import { CreateTransitionElement$Params } from '../fn/transition-element/create-transition-element';
import { createTransitionElementDiagram } from '../fn/transition-element/create-transition-element-diagram';
import { CreateTransitionElementDiagram$Params } from '../fn/transition-element/create-transition-element-diagram';
import { createTransitionElementProgress } from '../fn/transition-element/create-transition-element-progress';
import { CreateTransitionElementProgress$Params } from '../fn/transition-element/create-transition-element-progress';
import { createTransitionElementStory } from '../fn/transition-element/create-transition-element-story';
import { CreateTransitionElementStory$Params } from '../fn/transition-element/create-transition-element-story';
import { deleteTransitionElement } from '../fn/transition-element/delete-transition-element';
import { DeleteTransitionElement$Params } from '../fn/transition-element/delete-transition-element';
import { getTransitionElement } from '../fn/transition-element/get-transition-element';
import { GetTransitionElement$Params } from '../fn/transition-element/get-transition-element';
import { getTransitionElementStory } from '../fn/transition-element/get-transition-element-story';
import { GetTransitionElementStory$Params } from '../fn/transition-element/get-transition-element-story';
import { listTargets } from '../fn/transition-element/list-targets';
import { ListTargets$Params } from '../fn/transition-element/list-targets';
import { listTransitionElements } from '../fn/transition-element/list-transition-elements';
import { ListTransitionElements$Params } from '../fn/transition-element/list-transition-elements';
import { moveTransitionElement } from '../fn/transition-element/move-transition-element';
import { MoveTransitionElement$Params } from '../fn/transition-element/move-transition-element';
import { updateAssessment } from '../fn/transition-element/update-assessment';
import { UpdateAssessment$Params } from '../fn/transition-element/update-assessment';
import { updateTarget } from '../fn/transition-element/update-target';
import { UpdateTarget$Params } from '../fn/transition-element/update-target';
import { updateTransitionElement } from '../fn/transition-element/update-transition-element';
import { UpdateTransitionElement$Params } from '../fn/transition-element/update-transition-element';
import { updateTransitionElementDiagram } from '../fn/transition-element/update-transition-element-diagram';
import { UpdateTransitionElementDiagram$Params } from '../fn/transition-element/update-transition-element-diagram';
import { updateTransitionElementProgress } from '../fn/transition-element/update-transition-element-progress';
import { UpdateTransitionElementProgress$Params } from '../fn/transition-element/update-transition-element-progress';
import { updateTransitionElementStory } from '../fn/transition-element/update-transition-element-story';
import { UpdateTransitionElementStory$Params } from '../fn/transition-element/update-transition-element-story';

@Injectable({ providedIn: 'root' })
export class TransitionElementService extends BaseService {
  constructor(config: CoreApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listTransitionElements()` */
  static readonly ListTransitionElementsPath = '/v1/boards/{boardId}/transition-elements';

  /**
   * List transition elements.
   *
   * List all transition elements for a board
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTransitionElements()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTransitionElements$Response(
    params: ListTransitionElements$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListTransitionElementResponse>> {
    return listTransitionElements(this.http, this.rootUrl, params, context);
  }

  /**
   * List transition elements.
   *
   * List all transition elements for a board
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTransitionElements$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTransitionElements(
    params: ListTransitionElements$Params,
    context?: HttpContext
  ): Observable<ApiListTransitionElementResponse> {
    return this.listTransitionElements$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListTransitionElementResponse>): ApiListTransitionElementResponse => r.body)
    );
  }

  /** Path part for operation `createTransitionElement()` */
  static readonly CreateTransitionElementPath = '/v1/boards/{boardId}/transition-elements';

  /**
   * Create transition element.
   *
   * Create transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTransitionElement()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElement$Response(
    params: CreateTransitionElement$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiTransitionElementResponse>> {
    return createTransitionElement(this.http, this.rootUrl, params, context);
  }

  /**
   * Create transition element.
   *
   * Create transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTransitionElement$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElement(
    params: CreateTransitionElement$Params,
    context?: HttpContext
  ): Observable<ApiTransitionElementResponse> {
    return this.createTransitionElement$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiTransitionElementResponse>): ApiTransitionElementResponse => r.body)
    );
  }

  /** Path part for operation `getTransitionElement()` */
  static readonly GetTransitionElementPath = '/v1/boards/{boardId}/transition-elements/{id}';

  /**
   * Get transition element.
   *
   * Get a specific transition element with all its related data
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTransitionElement()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTransitionElement$Response(
    params: GetTransitionElement$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiTransitionElementResponse>> {
    return getTransitionElement(this.http, this.rootUrl, params, context);
  }

  /**
   * Get transition element.
   *
   * Get a specific transition element with all its related data
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTransitionElement$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTransitionElement(
    params: GetTransitionElement$Params,
    context?: HttpContext
  ): Observable<ApiTransitionElementResponse> {
    return this.getTransitionElement$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiTransitionElementResponse>): ApiTransitionElementResponse => r.body)
    );
  }

  /** Path part for operation `deleteTransitionElement()` */
  static readonly DeleteTransitionElementPath = '/v1/boards/{boardId}/transition-elements/{id}';

  /**
   * Delete transition element.
   *
   * Delete transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTransitionElement()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTransitionElement$Response(
    params: DeleteTransitionElement$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return deleteTransitionElement(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete transition element.
   *
   * Delete transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteTransitionElement$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTransitionElement(params: DeleteTransitionElement$Params, context?: HttpContext): Observable<string> {
    return this.deleteTransitionElement$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `updateTransitionElement()` */
  static readonly UpdateTransitionElementPath = '/v1/boards/{boardId}/transition-elements/{id}';

  /**
   * Update transition element.
   *
   * Update a transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTransitionElement()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElement$Response(
    params: UpdateTransitionElement$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateTransitionElement(this.http, this.rootUrl, params, context);
  }

  /**
   * Update transition element.
   *
   * Update a transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTransitionElement$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElement(params: UpdateTransitionElement$Params, context?: HttpContext): Observable<string> {
    return this.updateTransitionElement$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `createTransitionElementDiagram()` */
  static readonly CreateTransitionElementDiagramPath = '/v1/boards/{boardId}/transition-elements/{id}/diagram';

  /**
   * Create transition element diagram.
   *
   * Create a diagram for a transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTransitionElementDiagram()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementDiagram$Response(
    params: CreateTransitionElementDiagram$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiDiagramResponse>> {
    return createTransitionElementDiagram(this.http, this.rootUrl, params, context);
  }

  /**
   * Create transition element diagram.
   *
   * Create a diagram for a transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTransitionElementDiagram$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementDiagram(
    params: CreateTransitionElementDiagram$Params,
    context?: HttpContext
  ): Observable<ApiDiagramResponse> {
    return this.createTransitionElementDiagram$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiDiagramResponse>): ApiDiagramResponse => r.body)
    );
  }

  /** Path part for operation `updateTransitionElementDiagram()` */
  static readonly UpdateTransitionElementDiagramPath = '/v1/boards/{boardId}/transition-elements/{id}/diagram';

  /**
   * Update transition element diagram.
   *
   * Update diagram for a transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTransitionElementDiagram()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementDiagram$Response(
    params: UpdateTransitionElementDiagram$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateTransitionElementDiagram(this.http, this.rootUrl, params, context);
  }

  /**
   * Update transition element diagram.
   *
   * Update diagram for a transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTransitionElementDiagram$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementDiagram(
    params: UpdateTransitionElementDiagram$Params,
    context?: HttpContext
  ): Observable<string> {
    return this.updateTransitionElementDiagram$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `moveTransitionElement()` */
  static readonly MoveTransitionElementPath = '/v1/boards/{boardId}/transition-elements/{id}/move';

  /**
   * Move transition elements.
   *
   * Move transition elements to a new group
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `moveTransitionElement()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  moveTransitionElement$Response(
    params: MoveTransitionElement$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return moveTransitionElement(this.http, this.rootUrl, params, context);
  }

  /**
   * Move transition elements.
   *
   * Move transition elements to a new group
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `moveTransitionElement$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  moveTransitionElement(params: MoveTransitionElement$Params, context?: HttpContext): Observable<string> {
    return this.moveTransitionElement$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `createTransitionElementProgress()` */
  static readonly CreateTransitionElementProgressPath = '/v1/boards/{boardId}/transition-elements/{id}/progresses';

  /**
   * Create transition element progress.
   *
   * Create new progress for a transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTransitionElementProgress()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementProgress$Response(
    params: CreateTransitionElementProgress$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiProgressResponse>> {
    return createTransitionElementProgress(this.http, this.rootUrl, params, context);
  }

  /**
   * Create transition element progress.
   *
   * Create new progress for a transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTransitionElementProgress$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementProgress(
    params: CreateTransitionElementProgress$Params,
    context?: HttpContext
  ): Observable<ApiProgressResponse> {
    return this.createTransitionElementProgress$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiProgressResponse>): ApiProgressResponse => r.body)
    );
  }

  /** Path part for operation `updateTransitionElementProgress()` */
  static readonly UpdateTransitionElementProgressPath =
    '/v1/boards/{boardId}/transition-elements/{id}/progresses/{targetType}';

  /**
   * Update transition element progress.
   *
   * Update progress for a transition element
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTransitionElementProgress()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementProgress$Response(
    params: UpdateTransitionElementProgress$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateTransitionElementProgress(this.http, this.rootUrl, params, context);
  }

  /**
   * Update transition element progress.
   *
   * Update progress for a transition element
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTransitionElementProgress$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementProgress(
    params: UpdateTransitionElementProgress$Params,
    context?: HttpContext
  ): Observable<string> {
    return this.updateTransitionElementProgress$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `getTransitionElementStory()` */
  static readonly GetTransitionElementStoryPath = '/v1/boards/{boardId}/transition-elements/{id}/story';

  /**
   * Get transition element story.
   *
   * Get transition element story
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTransitionElementStory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTransitionElementStory$Response(
    params: GetTransitionElementStory$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiStoryResponse>> {
    return getTransitionElementStory(this.http, this.rootUrl, params, context);
  }

  /**
   * Get transition element story.
   *
   * Get transition element story
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTransitionElementStory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTransitionElementStory(
    params: GetTransitionElementStory$Params,
    context?: HttpContext
  ): Observable<ApiStoryResponse> {
    return this.getTransitionElementStory$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiStoryResponse>): ApiStoryResponse => r.body)
    );
  }

  /** Path part for operation `createTransitionElementStory()` */
  static readonly CreateTransitionElementStoryPath = '/v1/boards/{boardId}/transition-elements/{id}/story';

  /**
   * Create transition element story.
   *
   * Create transition element story
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTransitionElementStory()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementStory$Response(
    params: CreateTransitionElementStory$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return createTransitionElementStory(this.http, this.rootUrl, params, context);
  }

  /**
   * Create transition element story.
   *
   * Create transition element story
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTransitionElementStory$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTransitionElementStory(params: CreateTransitionElementStory$Params, context?: HttpContext): Observable<string> {
    return this.createTransitionElementStory$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `updateTransitionElementStory()` */
  static readonly UpdateTransitionElementStoryPath = '/v1/boards/{boardId}/transition-elements/{id}/story';

  /**
   * Update transition element story.
   *
   * Update transition element story
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTransitionElementStory()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementStory$Response(
    params: UpdateTransitionElementStory$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateTransitionElementStory(this.http, this.rootUrl, params, context);
  }

  /**
   * Update transition element story.
   *
   * Update transition element story
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTransitionElementStory$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTransitionElementStory(params: UpdateTransitionElementStory$Params, context?: HttpContext): Observable<string> {
    return this.updateTransitionElementStory$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `createAssessment()` */
  static readonly CreateAssessmentPath = '/v1/boards/{boardId}/transition-elements/{transitionElementId}/assessments';

  /**
   * Create assessment.
   *
   * Create assessment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAssessment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAssessment$Response(
    params: CreateAssessment$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return createAssessment(this.http, this.rootUrl, params, context);
  }

  /**
   * Create assessment.
   *
   * Create assessment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAssessment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAssessment(params: CreateAssessment$Params, context?: HttpContext): Observable<string> {
    return this.createAssessment$Response(params, context).pipe(map((r: StrictHttpResponse<string>): string => r.body));
  }

  /** Path part for operation `updateAssessment()` */
  static readonly UpdateAssessmentPath =
    '/v1/boards/{boardId}/transition-elements/{transitionElementId}/assessments/{id}';

  /**
   * Update assessment.
   *
   * Update assessment with id
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAssessment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAssessment$Response(
    params: UpdateAssessment$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateAssessment(this.http, this.rootUrl, params, context);
  }

  /**
   * Update assessment.
   *
   * Update assessment with id
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAssessment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAssessment(params: UpdateAssessment$Params, context?: HttpContext): Observable<string> {
    return this.updateAssessment$Response(params, context).pipe(map((r: StrictHttpResponse<string>): string => r.body));
  }

  /** Path part for operation `listTargets()` */
  static readonly ListTargetsPath = '/v1/boards/{boardId}/transition-elements/{transitionElementId}/targets';

  /**
   * List targets.
   *
   * List targets
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTargets()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTargets$Response(
    params: ListTargets$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListTargetsResponse>> {
    return listTargets(this.http, this.rootUrl, params, context);
  }

  /**
   * List targets.
   *
   * List targets
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTargets$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTargets(params: ListTargets$Params, context?: HttpContext): Observable<ApiListTargetsResponse> {
    return this.listTargets$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListTargetsResponse>): ApiListTargetsResponse => r.body)
    );
  }

  /** Path part for operation `createTarget()` */
  static readonly CreateTargetPath = '/v1/boards/{boardId}/transition-elements/{transitionElementId}/targets';

  /**
   * Create target.
   *
   * Create target
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTarget()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTarget$Response(params: CreateTarget$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return createTarget(this.http, this.rootUrl, params, context);
  }

  /**
   * Create target.
   *
   * Create target
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTarget$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTarget(params: CreateTarget$Params, context?: HttpContext): Observable<string> {
    return this.createTarget$Response(params, context).pipe(map((r: StrictHttpResponse<string>): string => r.body));
  }

  /** Path part for operation `updateTarget()` */
  static readonly UpdateTargetPath = '/v1/boards/{boardId}/transition-elements/{transitionElementId}/targets/{id}';

  /**
   * Update target.
   *
   * Update target with id
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTarget()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTarget$Response(params: UpdateTarget$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return updateTarget(this.http, this.rootUrl, params, context);
  }

  /**
   * Update target.
   *
   * Update target with id
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTarget$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTarget(params: UpdateTarget$Params, context?: HttpContext): Observable<string> {
    return this.updateTarget$Response(params, context).pipe(map((r: StrictHttpResponse<string>): string => r.body));
  }
}
